/** @jsxImportSource @emotion/react */
import printerImg from '../../../../assets/icons/printer.svg';
import rfidImg from '../../../../assets/icons/rfid.svg';
import * as React from 'react';
import { ReactNode } from 'react';
import * as R from 'ramda';
import { css } from '@emotion/react';
import ModalView, { ModalContent } from '../../../../layout/modal/ModalView';
import { connect } from 'react-redux';
import { IAppState } from '../../../../core/redux/IAppState';
import { getHousehold } from '../../../../guestSearch/module';
import { GuestDetails } from '../../../guestDetails';
import ErrorView from '../../../../forms/ErrorView';
import { BoxedErrorInverse } from '../../../../forms/elements/boxedMessage/boxedMessageStyles';
import { LargeText, UNSELECTABLE_TEXT, XLargeText } from '../../../../styles/styles';
import GuestPrintSummary from './GuestPrintSummary';
import { Column, Row } from '../../../../layout/flexbox';
import { BLUE_CREEK, LIGHT_GREY, MIDDLE_GREY, RED_CREEK } from '../../../../styles/colors';
import { reissueMediaAction } from './reissueMediaAction';
import { getMostRecentMedia } from '../module';
import LabelGroup, { REVERSE_ROW_GROUP } from '../../../../forms/elements/LabelGroup';
import { passPrintDisabledMessage } from '../../../featureDisabling';
import { BigNeutral, BigSecondary } from '../../../../forms/elements/buttons';
import { COLUMN_SPACING, ROW_SPACING } from '../../../../forms/styles';
import { PassPdf } from './passPdf';
import { isNilOrEmpty, not, assoc } from '../../../../language/module';
import { calculatePassReprintFee, DEFAULT_REPRINT_FEE, getPrintCountStartDate, NO_REPRINT_FEE, printPdf } from './module';
import { markGuestPdfPrintedAction } from './markGuestPdfPrintedAction';
import { Loader } from '../../../../forms/elements/loader/Loader';
import { getError, getLoading } from '../../../../core/redux/api/module';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { ApiError } from '../../../../core/redux/api/apiError';
import { MODAL_SIZE } from '../../../../layout/modal/modalSize';
import { clearApiErrorAction } from '../../../../core/redux/api/clearApiErrorAction';
import GuestLinkRFIDSummary from '../assocRfid/GuestLinkRfidSummary';
import { ChipId, ChipIdSet } from './chipId';
import { assocRfidAction } from '../assocRfid/assocRfidAction';
import ImmutableMap from '../../../../language/ImmutableMap';
import { findRfidAssocError } from '../assocRfid/module';
import { onIe } from '../../../../language/browser';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import { EntitlementType } from '../../../../entitlementTypes/entitlementType';
import { getResource } from '../../../../language/resources/getResource';
import { getSeasonReprintCountAction } from './getSeasonReprintCountAction';
import { PassPrintConfig } from '../../../../auth/config/passPrintConfig';


const styles = {
  ieCheckbox: !onIe() ? '' : css`margin-top: -14px; margin-right: 10px;`,
  alternatingRows: css`
    border: 1px solid ${MIDDLE_GREY};
    max-height: 400px;
    overflow-y: auto;
    
    & > div {
      &:nth-of-type(even) {
        background-color:  ${LIGHT_GREY};
      }
      
      &:not(:last-child) {
        border-bottom: 1px solid ${MIDDLE_GREY};
      }
    }
  `,
  selectAll:       css`
    ${REVERSE_ROW_GROUP};
    height: 38px; /* IE needs help on this one... */ 
    span { 
      font-weight: normal;
      ${UNSELECTABLE_TEXT};
    }
    input {
      zoom: 2;
    }
  `,
  reprintRow:      css`
    margin-top: 10px;
    display: flex;
    align-items: center;
  `,
  buttonsRow:      css`
    max-height: 50px;
    margin-top: 25px;
  `,
};

const reprintFeeBox = (fee: number | undefined) => css`
  margin-left: 10px;
  width: 50px;
  height: 20px;;
  background-color: ${((fee !== undefined && fee > NO_REPRINT_FEE) || fee === undefined) ? RED_CREEK : BLUE_CREEK};
  border-radius: 5px;
  text-align: center;
  padding: 7px 0px !important;
  color: WHITE;
`


type GuestPrintData = {
  guestId: string;
  entitlementTypeId: string | undefined;
}


type RfidInfo = {
  rfid: string,
  error: string | undefined,
};


type Props = {
  guestId: string;
  entitlementTypeId: number;
  guests?: ImmutableMap<GuestDetails>;
  eligibleGuests?: ImmutableMap<GuestDetails>;

  guestPdfs?: PassPdf[];
  isLoadingPdf?: boolean;
  printError?: ApiError;

  knownGuestRfidCodes?: ChipIdSet;

  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  resources: Record<string, any>;

  enablePassReprintNotifications: boolean;
  passReprintCountError?: ApiError;
  passPrintConfig: PassPrintConfig

  // Dispatch
  onDismiss: () => void;
  clearPrintError?: (guestId: string) => void;
  printGuests?: (guestsToPrint: GuestPrintData[]) => void;
  markPrinted?: (pdf: PassPdf) => void,
  assocRfid?: (guest: GuestDetails, chipId: string) => void;
  getSeasonReprintCounts?: (printCountStartDate: Date, masterGuestIds: string) => void;
  clearPassReprintCountError?: (masterGuestIds: string) => void;
};


type State = {
  error?: any;
  modalState: 'print' | 'loading' | 'rfid';
  selectedGuests: ImmutableMap<GuestDetails>;
  printError: string | undefined;
  rfidSubmitError: string | undefined;
  groupRfid: ImmutableMap<RfidInfo>;
  selectedEntitlements: ImmutableMap<string>;
  passReprintCountError: string | undefined;
  totalReprintFee: number | undefined;
};


/**
 * GroupPrintModal: A component that renders a modal for the user to select members of a household for printing. The
 * modal then guides the user through the print process all the way to associating RFID codes with guests' passes.
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => {
    const household = getHousehold(state, props.guestId) || [];

    return ({
      entitlementTypeId: state.resortToolsConfig.dtwCategoryId,
      resources: state.locale,
      guests: household.reduce(
        (running, next) => running.add(next.masterGuestId, state.guests.get(next.masterGuestId)),
        ImmutableMap.empty(),
      ),

      eligibleGuests: household.reduce(
        (running, next) => {
          const guest = state.guests.get(next.masterGuestId);

          return passPrintDisabledMessage(
            state.locale,
            state.resortToolsConfig.dtwCategoryId,
            guest,
            state.entitlementTypesDetails!.allEntitlementTypes
          ) ? running : running.add(next.masterGuestId, guest);
        },
        ImmutableMap.empty(),
      ),

      knownGuestRfidCodes: state.knownGuestRfidCodes,

      guestPdfs:    state.passPrinting.pdfs,
      isLoadingPdf: household.reduce(
        (isLoading, member) => isLoading
                               || !!getLoading(state, member.masterGuestId, ACTION_TYPE.REISSUE_MEDIA_REQUEST),
        false,
      ),
      printError:   R.path(
        [ 'errorMsg' ],
        R.head(
          household
            .map(member => getError(state, member.masterGuestId, ACTION_TYPE.REISSUE_MEDIA_REQUEST))
            .filter(not(isNilOrEmpty)),
        ),
      ),
      allEntitlementTypes: state.entitlementTypesDetails!.allEntitlementTypes,
      enablePassReprintNotifications: state.resortToolsConfig.enablePassReprintNotifications,
      passReprintCountError: getError(
        state,
        household.map(h => h.masterGuestId).join(','),
        ACTION_TYPE.GET_SEASON_REPRINT_COUNT_REQUEST,
      ),
      //passPrintConfig: state.resortToolsConfig.passPrintConfig,
    });
  },
  (dispatch) => ({
    printGuests:     (guestsToPrint: GuestPrintData[]) => dispatch(reissueMediaAction(guestsToPrint)),
    markPrinted:     (pdf: PassPdf) => dispatch(markGuestPdfPrintedAction(pdf)),
    clearPrintError: (guestId: string) => dispatch(clearApiErrorAction(guestId, ACTION_TYPE.REISSUE_MEDIA_REQUEST)),
    assocRfid:       (
                       guest: GuestDetails,
                       chipId: string,
                     ) => dispatch(assocRfidAction(guest, assoc('chipID', chipId, getMostRecentMedia(guest)!))),
    getSeasonReprintCounts: (printCountStartDate: Date, masterGuestIds: string) => dispatch(getSeasonReprintCountAction(printCountStartDate, masterGuestIds)),
    clearPassReprintCountError: (masterGuestIds: string) => 
      dispatch(clearApiErrorAction(masterGuestIds, ACTION_TYPE.REISSUE_MEDIA_REQUEST)),
  }),
)(class GroupPrintModal extends React.PureComponent<Props, State> {
  public state: State = {
    modalState:             'print',
    selectedGuests:         ImmutableMap.empty(),
    printError:             undefined,
    rfidSubmitError:        undefined,
    groupRfid:              ImmutableMap.empty(),
    selectedEntitlements:   ImmutableMap.empty(),
    passReprintCountError:  undefined,
    totalReprintFee:        0,
  };

  public componentDidMount() {
    if (this.props.enablePassReprintNotifications) {
      this.getSeasonReprintCount();
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (this.props.enablePassReprintNotifications) {
      if (!prevProps.passReprintCountError && this.props.passReprintCountError) {
        this.setState(prev => assoc('passReprintCountError', getResource(this.props.resources, 'REPRINT_COUNT_LOAD_ERROR'), prev))
        this.setState(prev => assoc('totalReprintFee', undefined, prev))
        const masterGuestIds = this.props.guests?.getKeys().join(",");
        if (masterGuestIds) {
          this.props.clearPassReprintCountError!(masterGuestIds);
       }
      }
      if (!this.state.passReprintCountError && !R.equals(this.state.selectedGuests, prevState.selectedGuests)) {
        /* Calculate total pass reprint fees if there was no error loading the pass counts */
        this.calculateTotalReprintFees();
      }
    }

     // Switch to Print Loading
    if (this.props.isLoadingPdf && prevState.modalState !== 'loading') {
      this.setState(prev => assoc('modalState', 'loading', prev));
    }

    // Switch to Print Error
    if (
      prevProps.isLoadingPdf &&
      !this.props.isLoadingPdf &&
      this.props.printError
    ) {
      this.setState(prev => assoc('modalState', 'print', prev));
    }

    // PDF list changed
    if (!R.equals(prevProps.guestPdfs, this.props.guestPdfs)) {
      const currentPDF = this.pdfFor(prevState.selectedGuests.getKeys()[0], prevProps.guestPdfs!);

      const nextPDF = this.pdfFor(this.state.selectedGuests.getKeys()[0], this.props.guestPdfs!);

      // A usable Print Pass PDF came through
      if (
        !R.equals(currentPDF, nextPDF) &&
        !isNilOrEmpty(R.path<string>(['pdf'], nextPDF))
      ) {
        // Open PDF in new tab for printing
        this.startPDFPrintInNewTab(nextPDF!.pdf);
        this.props.markPrinted!(nextPDF!);
      }
    }
  }

  public handleEntitlementChange = (key: string, value: string) => {
    this.setState(prev => assoc(
      'selectedEntitlements',
      prev.selectedEntitlements.add(key, value),
      prev,
    ));
  };

  private getSeasonReprintCount = () => {
    const printCountStartDate = getPrintCountStartDate(this.props.passPrintConfig);
    const masterGuestIds = this.props.guests?.getKeys().join(",");
    if (masterGuestIds) {
      this.props.getSeasonReprintCounts!(printCountStartDate, masterGuestIds);
    }
  }

  private cancelAndClose = () => {
    this.props.onDismiss(); // Close modal before dispatching a bunch of other actions
    this.props.guests!.map(guestId => this.props.clearPrintError!(guestId));
  };


  private pdfFor = (guestId: string, from: PassPdf[]) => R.head(from.filter(pdf => pdf.guestIds.includes(guestId)));


  private startPDFPrintInNewTab = (passPdf: string): void => printPdf(window, passPdf, this.onCompletePdfPrint);


  private isEligible = (
    guest: GuestDetails,
    entitlementTypes: ImmutableMap<EntitlementType>
  ): string | undefined => passPrintDisabledMessage(this.props.resources, this.props.entitlementTypeId, guest, entitlementTypes);


  private areAllSelected = () => this.props.eligibleGuests!.count() === this.state.selectedGuests.count();


  private onCompletePdfPrint = (success: boolean): void => {
    if (!success) {
      this.setState(prev => assoc('error', getResource(this.props.resources, 'PRINT_FAILURE'), prev));
      return;
    }

    this.setState(prev => ({...prev,
      error:      undefined,
      modalState: 'rfid',
      groupRfid:  this.state.selectedGuests.mapInto(() => ({ rfid: '', error: undefined })),
    }));
  };


  private onSelectAllClick = (isAllSelected: boolean) => () => {
    this.setState(
      prev => assoc(
        'selectedGuests',
        isAllSelected
          ? ImmutableMap.empty<GuestDetails>()
          : (this.props.eligibleGuests || ImmutableMap.empty<GuestDetails>()),
        prev,
      ),
    );
  };


  private onSelect = (guest: GuestDetails, isEligible: boolean) => () => {
    if (!isEligible) {
      return;
    }

    this.setState(prev => assoc(
      'selectedGuests',
      this.state.selectedGuests.has(guest.masterGuestId)
      ? this.state.selectedGuests.remove(guest.masterGuestId)
      : this.state.selectedGuests.add(guest.masterGuestId, guest),
      prev,
    ));
  };

  private calculateTotalReprintFees() {
    const totalFees = this.state.selectedGuests 
      .getValues()                       
      .map(guest => calculatePassReprintFee(guest.passReprintCount ?? 0, this.props.passPrintConfig?.defaultPassPrintFee))
      .reduce((sum, fee) => sum + fee, 0); 

    this.setState(prev => assoc('totalReprintFee', totalFees, prev));
  };

  private onPrintGuestPasses = () => {
    const guestsToPrint = this.state.selectedGuests.getValues().map(guest => ({
      guestId: guest.masterGuestId,
      entitlementTypeId: R.path<string>([ 'entitlementTypeId' ], this.state.selectedEntitlements.get(guest.masterGuestId)),
    }));

    this.props.printGuests!(guestsToPrint);
  };


  private getAllKnownRfidIds = (): ChipIdSet => this.props.knownGuestRfidCodes!.concat(
    this.state.groupRfid.map((guestId, model) => {
      const guest = this.props.guests!.get(guestId)!;

      return {
        guestId:   guestId,
        firstName: guest.firstName,
        lastName:  guest.lastName,
        chipIds:   guest.media.map(media => media.chipID).concat(model.rfid),
      } as ChipId;
    }),
  );


  private onRfidCodeChange = (rfid: string, guestId: string) => this.setState(prev => assoc(
    'groupRfid',
    prev.groupRfid.add(guestId, {
      // Do some validation in real time (only when code is 20 char or more for performance reasons)
      error: rfid.length >= 20 ? findRfidAssocError(this.props.resources, guestId, rfid, this.getAllKnownRfidIds()) : undefined,
      rfid:  rfid,
    }),
    prev,
  ));


  private onSubmitRfid = (): void => {
    // Validate
    const allKnownRfids = this.getAllKnownRfidIds();
    const validated     = this.state.groupRfid.mapInto((id, rfid) => assoc(
      'error',
      findRfidAssocError(this.props.resources, id, rfid.rfid, allKnownRfids),
      rfid,
    ));

    this.setState(prev => assoc('groupRfid', validated, prev));

    if (validated.any((id, rfid) => !isNilOrEmpty(rfid.error))) {
      this.setState(prev => assoc(
        'rfidSubmitError',
        getResource(this.props.resources, 'GROUP_PRINT_MODAL_FOUND_ERRORS', validated.countWhere((id, rfid) => !isNilOrEmpty(rfid.error))),
        prev
      ));
      return;
    }

    this.setState(prev => assoc('rfidSubmitError', undefined, prev));

    // Submit RFIDs
    this.state.groupRfid.map((guestId, rfid) => this.props.assocRfid!(this.props.guests!.get(guestId), rfid.rfid));
    this.props.onDismiss();
  };


  // Rendering
  private renderPrintSummary = (guests: GuestDetails[]): ReactNode[] => guests.map(
    guest => <GuestPrintSummary key={guest.masterGuestId}
                                guestId={guest.masterGuestId}
                                eligibilityError={this.isEligible(guest, this.props.allEntitlementTypes!)}
                                isSelected={this.state.selectedGuests.has(guest.masterGuestId)}
                                onSelect={this.onSelect}
                                resources={this.props.resources}
                                selectedEntitlements={this.state.selectedEntitlements}
                                onChange={this.handleEntitlementChange}
                                enablePassReprintNotifications={this.props.enablePassReprintNotifications}/>,
  );


  private renderRFIDSummary = (guests: GuestDetails[]): ReactNode[] => guests.map((guest, index) => (
    <GuestLinkRFIDSummary resources={this.props.resources}
                          key={guest.masterGuestId}
                          guestId={guest.masterGuestId}
                          chipId={
                            R.path([ 'rfid' ], this.state.groupRfid.get(guest.masterGuestId)) || ''
                          }
                          autoFocus={index === 0}
                          error={this.state.groupRfid.get(guest.masterGuestId).error}
                          onChipIdUpdate={this.onRfidCodeChange}/>
  ));

  private renderConfirmationButtons = () => (
    <Row justifyContent="flex-end" rootCss={ROW_SPACING}>
        <BigSecondary onClick={this.props.onDismiss}>
          {getResource(this.props.resources, 'GROUP_PRINT_MODAL_CANCEL')}
        </BigSecondary>
        <BigNeutral onClick={this.onPrintGuestPasses}
                  disabled={this.state.selectedGuests.count() === 0}>
                    {getResource(this.props.resources, 'GROUP_PRINT_MODAL_PRINT')}
        </BigNeutral>
    </Row>
  );

  private renderWithReprintFeeNotification = () => (
   <Row justifyContent="space-between" rootCss={ROW_SPACING}>
      <Column rootCss={css`margin-top: 20px;`}>
        <span>{getResource(this.props.resources, 'REPRINT_POLICY_FLAT_RATE', this.props.passPrintConfig?.defaultPassPrintFee ?? DEFAULT_REPRINT_FEE)}</span>
        <div css={styles.reprintRow}>
          <span css={css`margin-top: 5px;`}>{getResource(this.props.resources, 'REPRINT_APPLICABLE_FEES')}</span>
          <div 
            title={this.state.totalReprintFee === undefined ? getResource(this.props.resources, 'REPRINT_COUNT_LOAD_ERROR') : undefined} 
            css={reprintFeeBox(this.state.totalReprintFee)}>
              {this.state.totalReprintFee !== undefined ? `$${this.state.totalReprintFee}` : undefined}
          </div>
        </div>
      </Column>
      <div css={styles.buttonsRow}>
        {this.renderConfirmationButtons()}
      </div>
    </Row>
  );


  private renderContent = (props: Props, state: State): React.ReactNode => {
    switch (state.modalState) {
      case 'print':
        const allSelected = this.areAllSelected();
        const guestCount  = state.selectedGuests.count();

        return <>
          <Row alignItems='center' justifyContent='space-between' rootCss={css`margin-bottom: 5px !important;`}>
            <span css={XLargeText}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_GUEST_COUNT', guestCount)}</span>
            { 
              this.props.enablePassReprintNotifications && 
              <span css={XLargeText}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_REPRINT_FEES')}</span>
            }
            <LabelGroup label={getResource(this.props.resources, 'GROUP_PRINT_MODAL_LABEL')} rootCss={styles.selectAll}>
              <input type='checkbox'
                     checked={allSelected}
                     onChange={this.onSelectAllClick(allSelected)}
                     css={styles.ieCheckbox}/>
            </LabelGroup>
          </Row>
          <Column rootCss={styles.alternatingRows}>
            {props.guests && this.renderPrintSummary(props.guests.getValues())}
          </Column>

          <ErrorView error={state.printError}/>
          <ErrorView error={getResource(this.props.resources, 'GROUP_PRINT_MODAL_WARNING')}
                     boxStyle={BoxedErrorInverse}/>

          { 
            props.enablePassReprintNotifications ?
              <Column rootCss={css`margin-bottom: 50px;`}>
                <ErrorView error={state.passReprintCountError}/>
                { this.renderWithReprintFeeNotification() }
              </Column> :
              <Row justifyContent="flex-end" rootCss={ROW_SPACING}>
                { this.renderConfirmationButtons()} 
              </Row>
          }
        </>;

      case 'loading':
        return <Column alignItems='center' justifyContent='center' rootCss={COLUMN_SPACING}>
          <Loader/>
          <span css={LargeText}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_GENERATE_PDF')}</span>
        </Column>;

      case 'rfid':
        return <React.Fragment>
          <span css={css`margin-bottom: 5px !important; ${XLargeText};`}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_LINK_RFID')}</span>
          <Column rootCss={styles.alternatingRows}>
            {
              this.renderRFIDSummary(state.selectedGuests.getValues())
            }
          </Column>

          <ErrorView error={state.rfidSubmitError}/>

          <Row rootCss={ROW_SPACING} justifyContent='flex-end'>
            <BigSecondary onClick={props.onDismiss}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_CANCEL')}</BigSecondary>
            <BigNeutral onClick={this.onSubmitRfid}
                        disabled={state.selectedGuests.count() === 0}>{getResource(this.props.resources, 'GROUP_PRINT_MODAL_OKAY')}</BigNeutral>
          </Row>
        </React.Fragment>;
    }
  };


  public render = () => (
    <ModalView onDismiss={this.cancelAndClose}
               size={this.props.isLoadingPdf ? MODAL_SIZE.SMALL : MODAL_SIZE.DYNAMIC}
               dismissable={!this.props.isLoadingPdf}
               contentCss={css`
                 position: relative;
                 ${COLUMN_SPACING};
               `}>
      <ModalTitleHeader resources={this.props.resources}
                        heading={this.state.modalState === 'rfid' ? getResource(this.props.resources, 'GROUP_PRINT_MODAL_GROUP_LINK') : getResource(this.props.resources, 'GROUP_PRINT_MODAL_GROUP_PRINT')}
                        icon={this.state.modalState === 'rfid' ? rfidImg : printerImg}
                        onDismiss={this.props.onDismiss}
      />
      <ModalContent>
        {
          this.renderContent(this.props, this.state)
        }
      </ModalContent>
    </ModalView>
  );
});
